import React, { useEffect } from 'react';
import { useAvailableModels } from '../hooks/useAvailableModels';
import { theme } from '../styles/theme';

interface ModelSelectProps {
  value: string;
  onChange: (model: string) => void;
  /** Model to coerce to when `value` is missing/out-of-list, if it's present
   *  in the live catalogue (e.g. a cheap default). Falls back to models[0]
   *  when omitted or not available. ModelSelect stays generic — callers own
   *  the choice of which model name is "preferred". */
  preferredDefault?: string;
}

/** Flat, alphabetically-sorted AI-model dropdown fed from the LiteLLM proxy.
 *  Follows CLAUDE.md §6: fetched dropdown, loading/empty/error+Retry states,
 *  never a hardcoded list or free-text fallback. */
const ModelSelect: React.FC<ModelSelectProps> = ({ value, onChange, preferredDefault }) => {
  const { data: models, isLoading, isError, refetch } = useAvailableModels();

  // Decision: PDR-002 follow-up — once the live model list loads, a stale/invalid
  // `value` (e.g. a hardcoded default that predates the proxy's real catalogue)
  // must not remain the active selection while the <select> visually shows a
  // different option. This is state-sync-after-fetch, not data fetching itself,
  // so a useEffect here doesn't violate CLAUDE.md §4's TanStack Query rule.
  // Coercion target prefers `preferredDefault` (e.g. the cheap 'gemini-2.5-flash'
  // default) over the alphabetically-first — and often most expensive — model.
  useEffect(() => {
    if (models && models.length > 0 && !models.includes(value)) {
      const target = preferredDefault && models.includes(preferredDefault)
        ? preferredDefault
        : models[0];
      onChange(target);
    }
  }, [models, value, onChange, preferredDefault]);

  if (isLoading) {
    return <p data-testid="model-select-loading" className={theme.text.secondary}>Loading models…</p>;
  }
  if (isError) {
    return (
      <div data-testid="model-select-error">
        <p className="text-qc-error text-sm">Could not load the model list.</p>
        <button type="button" data-testid="model-select-retry" onClick={() => refetch()}
                className="mt-1 text-sm underline">Retry</button>
      </div>
    );
  }
  if (!models || models.length === 0) {
    return <p data-testid="model-select-empty" className={theme.text.secondary}>No models found.</p>;
  }
  return (
    <select
      data-testid="model-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
    >
      {models.map((m) => (
        <option key={m} value={m}>{m}</option>
      ))}
    </select>
  );
};

export default ModelSelect;
